import axios, { AxiosResponse } from 'axios';

import { costCalculatorApi, usersApi } from './api';

import { TProjectCompletionRes } from '@/components/molecules/JourneyProgress/JourneyProgress.types';
import { TPlotMap } from '@/components/molecules/PlotMap/PlotMap.types';
import { TPendingSurveyResponse } from '@/components/molecules/SurveyModal/SurveyModal.type';
import { IConsultantType } from '@/components/organism/dashboard/HireConsultants/Consultants.type';
import { TPlotLocationResponse } from '@/components/organism/dashboard/Profile/components/PlotModal/types';
import { StageData } from '@/components/organism/dashboard/Stages/Stage.type';
import { ActivateServiceRequestResponse } from '@/components/organism/dashboard/Stages/components/Service/Service.type';
import {
  RescheduleTSnaggingPayload,
  TGetSnaggingSiteVisitDateSlot,
  TSnaggingCompletionPayload,
  TSnaggingDateTimeSlot,
  TSnaggingReScheduledDateSlot,
  TSnaggingSchedulerWorkTypes,
} from '@/types/dashboardContext.type';
import { LocalizationTypes } from '@/types/localization.type';
import { ServiceRequestPayload } from '@/types/requests.type';
import {
  ApiResponse,
  HelpFormGetResponseType,
  HelpFormResponse,
  ProjectSummaryResponse,
  ServiceApiResponse,
  ServiceStagesResponse,
} from '@/types/response.type';
import { IMe, IUserLoginPreCheck } from '@/types/user.type';

/**
 * Login pre check API
 * @param {string} accessToken and adding Source to identify the login request.
 * @returns {Promise<ApiResponse<IUserLoginPreCheck>>}
 */
export const loginPreCheck = async (
  accessToken: string,
  source: string
): Promise<ApiResponse<IUserLoginPreCheck>> => {
  return axios({
    method: 'GET',
    url: `${process.env.NEXT_PUBLIC_USERS_API_BASE_URL}/users/login-prechecks?source=${source}`,
    // url: `${process.env.NEXT_PUBLIC_USERS_API_BASE_URL}/users/login-prechecks`,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
  });
};

/**
 * Get me API
 * @returns {Promise<ApiResponse<IMe>>}
 */
export const getMe = (): Promise<ApiResponse<IMe>> => {
  return usersApi.get('/users/me');
};

/**
 * Get stages api
 * @returns {Promise<ApiResponse<ServiceStagesResponse>>}
 */
export const getServiceStages = (
  locale: string
): Promise<ApiResponse<ServiceStagesResponse>> =>
  usersApi.get('/users/stages?locale=' + locale);

/**
 * Get current stage api
 * @returns {Promise<ApiResponse<ServiceStagesResponse>>}
 */
export const getCurrentStageApi = (
  locale: string,
  controller = new AbortController()
): Promise<ApiResponse<ServiceStagesResponse>> =>
  usersApi.get('/users/me/current-stage?locale=' + locale, {
    signal: controller?.signal,
  });

/**
 * Get stages api for specific stage
 * @returns {Promise<ApiResponse<ServiceStagesResponse>>}
 */

export const getStageByName = (
  stageName: string,
  locale: string,
  controller?: AbortController
): Promise<ApiResponse<ServiceStagesResponse>> => {
  const params = new URLSearchParams({
    stageName: stageName,
    locale: locale,
  });

  return usersApi.get(`/users/stages?${params.toString()}`, {
    signal: controller?.signal,
  });

  // return usersApi.get(`/users/stages?stageName=${stageName}&locale=${locale}`, {
  //   signal: controller?.signal,
  // });
};

// start hire consultants api
/**
 * generic request for active service requests
 * @returns {Promise<AxiosResponse<ActivateServiceRequestResponse>>}
 */
export const activateServiceRequest = (
  payload: ServiceRequestPayload
  // TODO: need to add return type after it is set from BE
): Promise<AxiosResponse<ActivateServiceRequestResponse>> =>
  usersApi.post('/users/service-requests', payload);

/**
 * Get bidderList
 * Post create service request
 * @returns {Promise<ServiceApiResponse<StageData[]>>}
 */
export const createConsultantServiceRequest = (): Promise<
  ServiceApiResponse<IConsultantType[]>
> => usersApi.post(`/users/service-requests/consultant-procurement`);

/**
 * Get bidderList
 * @returns {Promise<ServiceApiResponse<IConsultantType[]>>}
 */
export const getConsultantBidderList = (
  servicesReqId: string,
  type: string
  // requiredCount: number = 10
): Promise<ServiceApiResponse<IConsultantType[]>> =>
  usersApi.get(
    `/users/bidders-list?type=${type}&serviceRequestId=${servicesReqId}`
  );

/**
 * Get bidderList
 * @returns {Promise<ServiceApiResponse<IConsultantType[]>>}
 */
export const sendConsultantProcurementVendors = (
  servicesReqId: string,
  vendorsIds: string[]
): Promise<ServiceApiResponse<IConsultantType[]>> =>
  usersApi.post(
    `users/service-requests/consultant-procurement/shortlisted-vendors`,
    {
      clientIds: vendorsIds,
      serviceRequestId: servicesReqId,
    }
  );
// End hire consultants api

// Start hire contractors api
/**
 * Post create service request
 * @returns {Promise<ServiceApiResponse<StageData[]>>}
 */
export const createContractorServiceRequest = (): Promise<
  ServiceApiResponse<IConsultantType[]>
> =>
  usersApi.post(`/users/service-requests/contractor-procurement/bidder-list`);

/**
 * Get bidderList
 * @returns {Promise<ServiceApiResponse<IConsultantType[]>>}
 */
export const getContractorsBidderList = (
  servicesReqId: string,
  requiredCount: number = 10
): Promise<ServiceApiResponse<IConsultantType[]>> =>
  usersApi.get(
    `/users/bidders-list?type=contractors&serviceRequestId=${servicesReqId}&requiredCount=${requiredCount}`
  );

// End hire contractors api

export const sendContractorProcurementVendors = (
  servicesReqId: string,
  vendorsIds: string[]
): Promise<ServiceApiResponse<IConsultantType[]>> =>
  usersApi.post(
    `users/service-requests/contractor-procurement/shortlisted-vendors`,
    {
      vendorSalesforceIds: vendorsIds,
      serviceRequestId: servicesReqId,
    }
  );

/**
 * Patch ME api
 * TODO: need to remove from here and use profile.api.ts instead
 */
export const updateProfileData = (data: any): Promise<any> =>
  usersApi.patch(`/users/me`, data);

export const getSelectedConsultantList = (
  servicesReqId: string
): Promise<ServiceApiResponse<IConsultantType[]>> =>
  usersApi.get(
    `/users/service-requests/procurements/shortlisted-vendors/submitted?serviceRequestId=${servicesReqId}`
  );

export const setAwardedConsultant = (
  vendorId: string,
  serviceRequestId: string
): Promise<any> =>
  usersApi.post(`/users/service-requests/consultant-procurement/award-vendor`, {
    vendorId,
    serviceRequestId,
  });

export const getSelectedContractorList = (
  servicesReqId: string
): Promise<ServiceApiResponse<IConsultantType[]>> =>
  usersApi.get(
    `/users/service-requests/procurements/shortlisted-vendors/submitted?serviceRequestId=${servicesReqId}`
  );

export const setAwardedContractor = (
  vendorId: string,
  serviceRequestId: string
): Promise<any> =>
  usersApi.post(`/users/service-requests/contractor-procurement/award-vendor`, {
    vendorId,
    serviceRequestId,
  });

export const getBidderListConfiguration = () =>
  usersApi.get('/users/service-requests/bidder-list-configurations');

export const getConsultantDocumentTemplates = (serviceId: string) =>
  usersApi.get(
    `/users/me/service-requests/${serviceId}?stepTemplateId=T__Appointment_of_Consultant__TD__Additional_Design_Document&locale=en`
  );

export const getProjectCompletionData =
  async (): Promise<TProjectCompletionRes> => {
    const res = await usersApi.get('/users/project-percentage');

    return res.data?.data;
  };

export const getPlotDetailsData = async (): Promise<TPlotMap> => {
  const res = await usersApi.get('/users/me/plot');

  return res.data?.data;
};

/**
 * Get plot details by plotId and communityNumber
 * @param {string} plotId
 * @param {string} communityNumber
 * @param {LocalizationTypes} locale
 * @returns {Promise<ApiResponse<{ data: TPlotMap }>}
 */
export const getPlotDetailsBySitePlan = async (
  plotId: string,
  communityNumber: string,
  roadId: string,
  locale: LocalizationTypes
): Promise<TPlotMap> => {
  const res = await usersApi.patch('/users/update-user-plot-by-site-plan', {
    plotId,
    communityNumber,
    roadId,
    locale,
    save: false,
  });

  return res.data?.data;
};

/**
 * Get activated serivces list in home page
 * @param {string} plotId
 * @param {string} communityNumber
 * @param {LocalizationTypes} locale
 * @returns {Promise<ApiResponse<null>>} // returns 204 no data
 */
export const updatePlotDetails = async (
  plotId: string,
  communityNumber: string,
  roadId: string,
  locale: LocalizationTypes
): Promise<ApiResponse<null>> => {
  const res = await usersApi.patch('/users/update-user-plot-by-site-plan', {
    plotId,
    communityNumber,
    roadId,
    locale,
    save: true,
  });

  return res;
};

/**
 * Get activated serivces list in home page
 * @returns {Promise<ApiResponse<{ data: StageData[] }>}
 */
export const getActivatedServices = (
  locale: string,
  controller?: AbortController
): Promise<AxiosResponse<{ data: StageData[] }>> =>
  usersApi.get(`/users/me/active-services?locale=${locale}`, {
    signal: controller?.signal,
  });

/**
 * Get project summary data
 * @returns { Promise<AxiosResponse<{ data: ProjectSummaryResponse }>> }
 */
export const getProjectSummary = (): Promise<
  AxiosResponse<{ data: ProjectSummaryResponse }>
> => {
  return usersApi.get('/users/summary');
};

/**
 * generic request for activate optional step
 * @param {string} srID, represents service request ID
 * @param {string} stepTargetStatus
 * @param {boolean} override
 * @returns {Promise<AxiosResponse<ActivateServiceRequestResponse>>}
 */
//TODO: need to update return type
export const activateOptionalStep = (
  srID: string,
  stepTargetStatus: string,
  override = false
): Promise<AxiosResponse<ActivateServiceRequestResponse>> =>
  usersApi.patch(`/users/service-requests/steps/${srID}?override=${override}`, {
    stepTargetStatus: stepTargetStatus,
  });

export const ActivateOnHoldProject = async (): Promise<any> => {
  const res = await usersApi.patch('users/me/activate-on-hold-project');
  return res.data;
};

/**
 * Not used anymore
 * @param locationLink
 * @returns
 */
export const getPlotLocationByLink = async (
  locationLink: string
): Promise<TPlotLocationResponse> => {
  const res = await usersApi.get(`/users/url-plot-details?url=${locationLink}`);

  return res.data;
};

/**
 * Not used anymore
 * @param locationLink
 * @returns
 */
export const getPlotLocationByCoordination = async (
  lat: number,
  lang: number
): Promise<TPlotLocationResponse> => {
  const res = await usersApi.get(
    `/users/plot-details?latitude=${lat}&longitude=${lang}`
  );

  return res.data;
};

export const getUserPendingSurvey =
  async (): Promise<TPendingSurveyResponse> => {
    const res = await usersApi.get(`/users/me/pending-survey`);
    return res.data;
  };

export const skipCurrentSurvey = async (): Promise<AxiosResponse> => {
  const res = await usersApi.post(`/users/surveys/skip`);
  return res;
};

export const getHelpForms = async (
  locale: string
): Promise<AxiosResponse<HelpFormGetResponseType>> => {
  return await usersApi.get(
    `/users/service-requests/help-forms?locale=${locale}`
  );
};

export const submitHelpForms = async (payload: {
  service: string;
  reason: string;
  additionalComment?: string;
}): Promise<AxiosResponse<HelpFormResponse>> => {
  return await usersApi.post('/users/service-requests/help-forms', payload);
};

export const restartServiceRequest = async (): Promise<AxiosResponse> => {
  return await usersApi.post('/users/service-requests/restart-project');
};

/**
 * Get FilterBidderList data for consultant and Contractor
 * @returns {Promise<ServiceApiResponse<IConsultantType[]>>}
 */
export const getBidderList = async (
  searchParams: Record<string, any>,
  maxCount = 10,
  type = '',
  locale = 'ar',
  serviceRequestId: any
) => {
  const filterDicts: Record<string, any> = {};
  const excludeParams = ['page', 'limit', 'sort', 'order'];
  searchParams.forEach((value: string, key: any) => {
    if (excludeParams.includes(key)) return;
    filterDicts[`${key}`] = value;
  });
  filterDicts['type'] = type;
  filterDicts['requiredCount'] = maxCount;
  filterDicts['locale'] = locale;
  filterDicts['serviceRequestId'] = serviceRequestId;
  if (serviceRequestId == undefined) {
    return;
  } else {
    const response = await usersApi.get(`/users/bidders-list`, {
      params: filterDicts,
    });
    return response;
  }
};

export const deactivateService = async (payload: {
  reason: string;
  order: string;
  caseId: string;
  appointmentId: string;
}): Promise<AxiosResponse> => {
  return await usersApi.post('/scheduler/cancel-appointment', payload);
};

/**
 * Get me getVillaList API
 * @returns {Promise<ApiResponse<IMe>>}
 */
export const getVillaList = (): Promise<ApiResponse<IMe>> => {
  return costCalculatorApi.get('cost-calculators/villa');
};

/**
 * Get me deleteVilla API
 * @returns {Promise<ApiResponse<IMe>>}
 */

export const deleteVilla = async (payload: {
  id: string;
  villaType: string;
}): Promise<AxiosResponse> => {
  return costCalculatorApi.post(
    'cost-calculators/delete-selected-villa',
    payload
  );
};

/**
 * Post Confirm villa API
 * @returns {Promise<ApiResponse<IMe>>}
 */

export const confirmVilla = async (payload: {
  id: string;
  villaType: string;
}): Promise<AxiosResponse> => {
  return usersApi.post('users/confirm-villa', payload);
};
/* Vendor Rating Submit API */

export const vendorRatingSubmit = async (
  payload: any
): Promise<AxiosResponse> => {
  return await usersApi.post('/users/submit-survey-response', payload);
};

/* get Notification */

export const getNotify = async (): Promise<AxiosResponse> => {
  return await usersApi.get('notifications');
};

/* get Notification */

export const readNotify = async (
  id: string,
  isRead: boolean
): Promise<AxiosResponse> => {
  const payload: any = {
    isRead: isRead,
  };
  const response = await usersApi.patch(`notifications/${id}`, payload);
  return response;
};

/* get StageService status */

export const getStageServiceStatus = async (
  serviceRequestId: string | undefined
): Promise<ApiResponse<IMe>> => {
  const response = await usersApi.get(
    `users/service-requests/status?serviceRequestId=${serviceRequestId}`
  );
  return response;
};

/* For delisted Vendor Confirmation */

export const updateDeListedVendor = async (payload: any) => {
  return await usersApi.post('/users/continue-engagement', payload);
};


/* Get scheduler API - Fetching available SF work types */

const SCHEDULER_TYPE = 'build-journey';

export const getSchedulerWorkTypes =
  async (): Promise<TSnaggingSchedulerWorkTypes> => {
    const res = await usersApi.get(`scheduler/work-types?type=${SCHEDULER_TYPE}`);
    return res?.data;
  };

/* Get Snagging Calender date and time API */

export const getSnaggingDateTimeSlot =
  async (locale: string): Promise<TSnaggingDateTimeSlot> => {
    const workTypes = await getSchedulerWorkTypes();
    const workTypeId = workTypes?.data?.[0]?.workTypeId;
    const res = await usersApi.get(`scheduler/time-slots?type=${SCHEDULER_TYPE}&workTypeId=${workTypeId}&locale=${locale}`);
    return res?.data?.data;
  };


export const getReScheduledDateSlot =
  async (locale: string, appointmentId: string): Promise<TSnaggingReScheduledDateSlot> => {
    const workTypes = await getSchedulerWorkTypes();
    const workTypeId = workTypes?.data?.[0]?.workTypeId;
    const res = await usersApi.get(`scheduler/time-slots?type=${SCHEDULER_TYPE}&workTypeId=${workTypeId}&existingAppId=${appointmentId}&locale=${locale}`);
    return res?.data?.data;
  };


/* Post  Submit snagging API */

export const completeSnaggingSiteVisit = async (
  payload: TSnaggingCompletionPayload
) => {
  return await usersApi.post(`scheduler/book-appointment`, payload);
};

export const completeReScheduledDateSlot = async (
  payload: RescheduleTSnaggingPayload
) => {
  return await usersApi.post(`/scheduler/reschedule-appointment`, payload);
};

export const getScheduledDateSlot = async (
  payload: TGetSnaggingSiteVisitDateSlot
) => {
  const res = await usersApi.post(
    `users/snagging-visit/scheduled-slot`,
    payload
  );
  return res?.data;
};

export const getHandoverData = async () => {
  const res = await usersApi.get('users/handover-document');
  return res?.data;
};

/* Added API for DMT service stages */
export const getDMTServiceStages = async (
  locale: string,
  controller?: AbortController
) => {
  const res = await usersApi.get(`users/dmt-stages?locale=${locale}`, {
    signal: controller?.signal,
  });
  return res?.data;
};
